
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Message, Trip } from '../types';
import { toast } from 'sonner';

interface UseMessageHandlingResult {
  messages: Message[];
  isLoading: boolean;
  sendMessage: (message: string) => Promise<void>;
  setInitialMessage: (destination: string) => void;
}

type DatabaseMessage = {
  content: string;
  role: 'user' | 'assistant';
  thread_id: string;
  trip_id: string;
  created_at: string;
}

export const useMessageHandling = (threadId: string | null, trip: Trip | null): UseMessageHandlingResult => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialMessageSet, setInitialMessageSet] = useState(false);

  // Load saved messages when threadId is available
  useEffect(() => {
    const fetchSavedMessages = async () => {
      if (!threadId || !trip?.id) return;
      
      try {
        const { data: dbMessages, error } = await supabase
          .from('assistant_chat_messages')
          .select('*')
          .eq('thread_id', threadId)
          .eq('trip_id', trip.id)
          .order('created_at', { ascending: true });
        
        if (error) {
          console.error('Error fetching saved messages:', error);
          return;
        }
        
        if (dbMessages && dbMessages.length > 0) {
          const formattedMessages: Message[] = (dbMessages as DatabaseMessage[]).map(msg => ({
            role: msg.role as 'user' | 'assistant',
            content: msg.content
          }));
          
          setMessages(formattedMessages);
          setInitialMessageSet(true);
        }
      } catch (error) {
        console.error('Error in fetchSavedMessages:', error);
      }
    };
    
    fetchSavedMessages();
  }, [threadId, trip?.id]);

  // Save message to database
  const saveMessageToDatabase = async (message: Message) => {
    if (!threadId || !trip?.id) return;
    
    try {
      const { error } = await supabase
        .from('assistant_chat_messages')
        .insert({
          trip_id: trip.id,
          thread_id: threadId,
          role: message.role,
          content: message.content
        } as DatabaseMessage);
      
      if (error) {
        console.error('Error saving message to database:', error);
      }
    } catch (error) {
      console.error('Error in saveMessageToDatabase:', error);
    }
  };

  const setInitialMessage = (destination: string) => {
    if (initialMessageSet) return;
    
    const welcomeMessage: Message = { 
      role: 'assistant', 
      content: `Welcome to the ${destination} Travel Assistant! Ask me anything about ${destination} such as places to visit, local cuisine, transportation, or cultural tips.`
    };
    
    setMessages([welcomeMessage]);
    saveMessageToDatabase(welcomeMessage);
    setInitialMessageSet(true);
  };

  const sendMessage = async (message: string) => {
    if (!message.trim() || !threadId || !trip) return;
    
    const userMessage: Message = { role: 'user', content: message };
    setMessages(prev => [...prev, userMessage]);
    saveMessageToDatabase(userMessage);
    setIsLoading(true);

    try {
      console.log('Sending message to assistant:', message);
      const { data, error } = await supabase.functions.invoke('travel-assistant', {
        body: { 
          action: 'sendMessage',
          threadId,
          message,
          destination: trip.destination
        }
      });
      
      if (error) {
        console.error('Error invoking travel-assistant function:', error);
        throw error;
      }
      
      if (!data?.response) {
        throw new Error('No response received from assistant');
      }
      
      console.log('Received assistant response');
      const assistantMessage: Message = { 
        role: 'assistant', 
        content: data.response
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      saveMessageToDatabase(assistantMessage);
      
    } catch (error) {
      console.error('Error getting assistant response:', error);
      toast.error('Failed to get response. Please try again.');
      
      const errorMessage: Message = { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error processing your request. Please try again.'
      };
      
      setMessages(prev => [...prev, errorMessage]);
      saveMessageToDatabase(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    isLoading,
    sendMessage,
    setInitialMessage
  };
};
